/**
 * FilterRow.tsx — Pills de filtro + dropdown Urgencia (a la DERECHA)
 *
 * Resuelve inconsistencia UI-04: filtros consistentes con P1/P2
 * Urgencia separada a la derecha para mejorar lectura y accesibilidad
 *
 * UID-03b | Fase 3 | feat/step3-coteja
 * @date 2026-03-06
 */

import type { FC, CSSProperties, ReactNode } from 'react';
import { colors, typography, borderRadius } from '@/design-system/v2';
import { UrgenciaDropdown } from './UrgenciaDropdown';
import type { VistaFiltro, UrgenciaNivel } from './types';

interface FilterCounts {
  todos: number;
  ausentes: number;
  incluidos: number;
  sobrantes: number;
}

interface FilterRowProps {
  vista: VistaFiltro;
  onVista: (v: VistaFiltro) => void;
  urgencias: UrgenciaNivel[];
  onUrgencias: (vals: UrgenciaNivel[]) => void;
  counts: FilterCounts;
  /**
   * T3 · §6 — ranura del buscador global, a la IZQUIERDA de los contadores-
   * filtro (buscar y filtrar son operaciones hermanas). Típicamente un
   * `<BuscadorGlobal/>` cableado por el consumidor. Opcional.
   */
  buscador?: ReactNode;
}

const C_AUS = colors.error;
const C_INC = colors.success;
const C_SOB = colors.info;

const VISTAS: Array<{ v: VistaFiltro; label: string; color: string }> = [
  { v: 'todos',     label: 'Todos',     color: colors.fg.primary  },
  { v: 'ausentes',  label: 'Ausentes',  color: C_AUS               },
  { v: 'incluidos', label: 'Incluidos', color: C_INC               },
  { v: 'sobrantes', label: 'Sobrantes', color: C_SOB               },
];

export const FilterRow: FC<FilterRowProps> = ({
  vista, onVista, urgencias, onUrgencias, counts, buscador,
}) => {
  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
    padding: '8px 0',
    marginBottom: '12px',
    fontFamily: typography.fontFamily.sans,
  };

  const pillStyle = (v: VistaFiltro, color: string): CSSProperties => {
    const active = vista === v;
    return {
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '5px 11px', borderRadius: borderRadius.full, cursor: 'pointer',
      border: `1px solid ${active ? color : colors.border.DEFAULT}`,
      background: active ? `${color}18` : 'transparent',
      color: active ? color : colors.fg.secondary,
      fontSize: typography.fontSize.sm,
      fontWeight: active ? typography.fontWeight.semibold : typography.fontWeight.normal,
      transition: 'all 0.15s',
    };
  };

  const badgeStyle = (v: VistaFiltro, color: string): CSSProperties => {
    const active = vista === v;
    return {
      fontFamily: typography.fontFamily.mono,
      fontSize: typography.fontSize.xs,
      padding: '1px 5px', borderRadius: borderRadius.full,
      background: active ? `${color}28` : colors.bg.tertiary,
      color: active ? color : colors.fg.muted,
    };
  };

  const dividerStyle: CSSProperties = {
    color: colors.border.DEFAULT,
    margin: '0 6px',
    userSelect: 'none',
    fontSize: typography.fontSize.base,
  };

  return (
    <div style={rowStyle}>
      {/* ── Buscador global (extremo izquierdo, §6) ─────────────── */}
      {buscador != null && (
        <>
          {buscador}
          <span style={dividerStyle}>│</span>
        </>
      )}

      {/* ── Pills de vista (izquierda) ──────────────────────────── */}
      {VISTAS.map(({ v, label, color }) => (
        <button key={v} type="button" onClick={() => onVista(v)}
          style={pillStyle(v, color)}>
          {label}
          <span style={badgeStyle(v, color)}>{counts[v]}</span>
        </button>
      ))}

      {/* ── Separador vertical ──────────────────────────────────── */}
      <span style={dividerStyle}>│</span>

      {/* ── Dropdown Urgencia (extremo derecho) ─────────────────── */}
      <div style={{ marginLeft: 'auto' }}>
        <UrgenciaDropdown selected={urgencias} onChange={onUrgencias} />
      </div>
    </div>
  );
};
